// Pure logic for the Automations panel: condition operators per field type,
// value parsing, placeholders, draft checks and run status display. No React
// or store imports, so it is unit-testable.
import type { ActionStep, Condition, ConditionOp, FieldDefinition, FieldType } from '@dunea/plugin-sdk';
import type {
  AutomationAction,
  AutomationDraft,
  AutomationRun,
  AutomationTrigger,
  AutomationView,
  RunStatus,
  TriggerField,
} from '~/lib/api/automations';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export { apiErrorMessage } from '~/components/workbench/tools/payments/payments-model';

// ─── conditions ───

export const OP_LABELS: Record<ConditionOp, string> = {
  eq: 'is',
  neq: 'is not',
  gt: 'is more than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  contains: 'contains',
  not_contains: 'does not contain',
  in: 'is one of',
  exists: 'is set',
  not_exists: 'is empty',
};

const NUMERIC: readonly FieldType[] = ['integer', 'decimal'];

/** Operators that make sense for a field of this type. */
export function opsFor(type: FieldType | undefined): ConditionOp[] {
  if (type && NUMERIC.includes(type)) {
    return ['gte', 'gt', 'lte', 'lt', 'eq', 'neq', 'exists', 'not_exists'];
  }

  if (type === 'boolean') {
    return ['eq', 'neq'];
  }

  if (type === 'select') {
    return ['eq', 'neq', 'in', 'exists', 'not_exists'];
  }

  return ['eq', 'neq', 'contains', 'not_contains', 'in', 'exists', 'not_exists'];
}

export function opNeedsValue(op: ConditionOp): boolean {
  return op !== 'exists' && op !== 'not_exists';
}

/** Owner-typed text → the value the condition compares with (numbers, booleans, comma lists). */
export function parseConditionValue(type: FieldType | undefined, op: ConditionOp, raw: string): unknown {
  if (!opNeedsValue(op)) {
    return undefined;
  }

  const one = (s: string): unknown => {
    const t = s.trim();

    if (type && NUMERIC.includes(type)) {
      return t !== '' && Number.isFinite(Number(t)) ? Number(t) : t;
    }

    if (type === 'boolean') {
      return t === 'true';
    }

    return t;
  };

  if (op === 'in') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(one);
  }

  return one(raw);
}

/** A condition value back to the text the owner edits. */
export function conditionValueText(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).join(', ');
  }

  return value === undefined || value === null ? '' : String(value);
}

export function fieldFor(trigger: AutomationTrigger | undefined, path: string): TriggerField | undefined {
  return trigger?.fields.find((f) => f.path === path);
}

/** A new condition row on the trigger's first field. */
export function newCondition(trigger: AutomationTrigger | undefined): Condition {
  const field = trigger?.fields[0];
  const op = opsFor(field?.type)[0];

  return {
    path: field?.path ?? 'mode',
    op,
    value: field?.type === 'boolean' ? true : field?.type === 'select' ? field.options?.[0]?.value : undefined,
  };
}

/** Plain-language condition: "Order total is at least 500000". */
export function describeCondition(trigger: AutomationTrigger | undefined, c: Condition): string {
  const label = fieldFor(trigger, c.path)?.label ?? c.path;
  const value = opNeedsValue(c.op) ? ` ${conditionValueText(c.value)}` : '';

  return `${label} ${OP_LABELS[c.op]}${value}`;
}

// ─── placeholders ───

export interface Placeholder {
  token: string;
  label: string;
  group: string;
}

/** {{placeholders}} available to step `stepIndex`: the trigger's fields, then earlier steps' outputs. */
export function placeholdersFor(
  trigger: AutomationTrigger | undefined,
  steps: readonly ActionStep[],
  stepIndex: number,
  actions: readonly AutomationAction[],
): Placeholder[] {
  const out: Placeholder[] = (trigger?.fields ?? []).map((f) => ({
    token: `{{${f.path}}}`,
    label: f.label,
    group: trigger?.title ?? 'Event',
  }));

  steps.slice(0, stepIndex).forEach((step, i) => {
    const def = actions.find((a) => a.id === step.action);

    for (const o of def?.outputs ?? []) {
      out.push({
        token: `{{steps.${i}.${o.key}}}`,
        label: o.label,
        group: `Step ${i + 1}: ${def?.title ?? step.action}`,
      });
    }
  });

  return out;
}

/** Field types whose input is free text, so a placeholder can be typed into it. */
const TEXT_TYPES: readonly FieldType[] = ['single_line_text', 'multi_line_text', 'rich_text', 'url', 'email'];

export function textFieldKeys(defs: readonly FieldDefinition[]): string[] {
  return defs.filter((d) => !d.list && TEXT_TYPES.includes(d.type)).map((d) => d.key);
}

/** Append a placeholder to a text value with a separating space. */
export function insertPlaceholder(current: unknown, token: string): string {
  const text = typeof current === 'string' ? current : '';
  return text && !/\s$/.test(text) ? `${text} ${token}` : `${text}${token}`;
}

/** The input key a focused element belongs to (FieldsForm ids are `${base}-${key}`). */
export function keyFromFieldId(id: string | undefined, keys: readonly string[]): string | null {
  if (!id) {
    return null;
  }

  const key = id.slice(id.lastIndexOf('-') + 1);

  return keys.includes(key) ? key : null;
}

const PLACEHOLDER = /\{\{\s*[a-zA-Z_][a-zA-Z0-9_.]*\s*\}\}/;

export function isTemplated(v: unknown): boolean {
  return typeof v === 'string' && PLACEHOLDER.test(v);
}

// ─── drafts ───

export function emptyDraft(trigger?: AutomationTrigger): AutomationDraft {
  return { name: '', trigger: trigger?.id ?? '', conditions: { all: [], any: [] }, actions: [], enabled: true };
}

export function draftFrom(a: AutomationView): AutomationDraft {
  return {
    name: a.name,
    trigger: a.trigger,
    conditions: { all: [...(a.conditions?.all ?? [])], any: [...(a.conditions?.any ?? [])] },
    actions: a.actions.map((s) => ({ ...s, input: { ...(s.input ?? {}) } })),
    enabled: a.enabled,
  };
}

/** A new step with the action's defaults. */
export function newStep(action: AutomationAction): ActionStep {
  const input: Record<string, unknown> = {};

  for (const f of action.input) {
    if (f.default !== undefined) {
      input[f.key] = f.default;
    }
  }

  return { action: action.id, input };
}

export function moveItem<T>(list: readonly T[], index: number, delta: -1 | 1): T[] {
  const to = index + delta;

  if (to < 0 || to >= list.length) {
    return [...list];
  }

  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];

  return next;
}

export function removeAt<T>(list: readonly T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}

/** Inputs without empty values (so optional fields aren't sent as ""). */
export function cleanInput(input: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(input).filter(
      ([, v]) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === ''),
    ),
  );
}

/**
 * Client-side checks before saving (studio re-checks everything): a name, a
 * known trigger, at least one step, every step known, fitting and with its
 * required inputs. Keys: 'name', 'trigger', 'actions', 'actions.<i>'.
 */
export function draftProblems(
  draft: AutomationDraft,
  triggers: readonly AutomationTrigger[],
  actions: readonly AutomationAction[],
): Record<string, string> {
  const problems: Record<string, string> = {};

  if (draft.name.trim().length < 2) {
    problems.name = 'Give it a name (2+ characters)';
  }

  if (!triggers.some((t) => t.id === draft.trigger)) {
    problems.trigger = 'Choose what starts it';
  }

  if (!draft.actions.length) {
    problems.actions = 'Add at least one step';
  }

  draft.actions.forEach((step, i) => {
    const def = actions.find((a) => a.id === step.action);

    if (!def) {
      problems[`actions.${i}`] = 'This action is no longer available';
      return;
    }

    if (def.unavailable) {
      problems[`actions.${i}`] = def.unavailable;
      return;
    }

    const missing = def.input.find((f) => {
      const v = step.input?.[f.key];
      return f.required && (v === undefined || v === null || v === '');
    });

    if (missing) {
      problems[`actions.${i}`] = `${missing.label} is required`;
    }
  });

  return problems;
}

export function actionTitle(actions: readonly AutomationAction[], id: string): string {
  return actions.find((a) => a.id === id)?.title ?? id;
}

/** "Order paid → Email the site owner, Create a single-use discount code". */
export function automationSummary(
  a: Pick<AutomationView, 'trigger' | 'triggerTitle' | 'actions'>,
  actions: readonly AutomationAction[],
): string {
  const steps = a.actions.map((s) => actionTitle(actions, s.action)).join(', ');
  return `${a.triggerTitle ?? a.trigger} → ${steps || 'no steps'}`;
}

/** Triggers grouped for the picker, in catalog order. */
export function groupTriggers(
  triggers: readonly AutomationTrigger[],
): { group: string; triggers: AutomationTrigger[] }[] {
  const groups: { group: string; triggers: AutomationTrigger[] }[] = [];

  for (const t of triggers) {
    const g = groups.find((x) => x.group === t.group);

    if (g) {
      g.triggers.push(t);
    } else {
      groups.push({ group: t.group, triggers: [t] });
    }
  }

  return groups;
}

// ─── runs ───

const RUN_META: Record<RunStatus, { tone: Tone; label: string }> = {
  pending: { tone: 'info', label: 'Queued' },
  running: { tone: 'accent', label: 'Running' },
  succeeded: { tone: 'success', label: 'Done' },
  failed: { tone: 'danger', label: 'Failed' },
  skipped: { tone: 'muted', label: 'Skipped' },
};

export function runStatusMeta(run: Pick<AutomationRun, 'status' | 'attempts' | 'step' | 'results'>): {
  tone: Tone;
  label: string;
} {
  // A pending run that already made progress is either retrying or waiting.
  if (run.status === 'pending' && run.attempts > 0) {
    return { tone: 'warning', label: 'Retrying' };
  }

  if (run.status === 'pending' && run.step > 0 && run.results[run.step - 1]?.action === 'flow.wait') {
    return { tone: 'info', label: 'Waiting' };
  }

  return RUN_META[run.status] ?? RUN_META.pending;
}

export const RUN_FILTERS: readonly (readonly ['' | RunStatus, string])[] = [
  ['', 'All'],
  ['succeeded', 'Done'],
  ['failed', 'Failed'],
  ['pending', 'Queued'],
  ['skipped', 'Skipped'],
];

/** "Never", "Just now", "5 min ago", "3 h ago", "2 d ago". */
export function lastRunLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) {
    return 'Never run';
  }

  const ms = now.getTime() - new Date(iso).getTime();

  if (!Number.isFinite(ms)) {
    return 'Never run';
  }

  const min = Math.floor(ms / 60_000);

  if (min < 1) {
    return 'Ran just now';
  }

  if (min < 60) {
    return `Ran ${min} min ago`;
  }

  const h = Math.floor(min / 60);

  return h < 24 ? `Ran ${h} h ago` : `Ran ${Math.floor(h / 24)} d ago`;
}

/** Studio's 400 `errors` map (actions.0.input.url → message) → per-step messages. */
export function stepErrorsFromApi(err: unknown): Record<string, string> {
  const errors = (err as { response?: { data?: { errors?: unknown } } } | null)?.response?.data?.errors;
  const out: Record<string, string> = {};

  if (errors && typeof errors === 'object') {
    for (const [key, message] of Object.entries(errors as Record<string, unknown>)) {
      const m = /^actions\.(\d+)/.exec(key);

      if (m && typeof message === 'string' && !out[`actions.${m[1]}`]) {
        out[`actions.${m[1]}`] = message;
      }
    }
  }

  return out;
}
